/**
 * Wrappers tipados para /api/clientes (§8 SRS).
 *
 * El backend descifra la PII automáticamente: legal_name, tax_id, address y
 * reception_contact llegan en texto plano.
 */

import { apiClient } from './apiClient'

export type Client = {
  id: number
  alias: string
  legal_name: string
  tax_id: string
  address: string | null
  plant: string | null
  reception_contact: string | null
  email: string | null
  created_at: string
  updated_at: string
}

export type ClientCreatePayload = {
  alias: string
  legal_name: string
  tax_id: string
  address?: string
  plant?: string
  reception_contact?: string
  email?: string
}

export type ClientUpdatePayload = Partial<ClientCreatePayload>

export type ListPage = {
  items: Client[]
  next_cursor: number | null
  total: number
}

export type SearchResponse = {
  items: Client[]
  query: string
}

export const clientesClient = {
  /** Lista paginada cursor-based (CLI-01). */
  list: (cursor?: number | null, limit = 25) => {
    const qs = new URLSearchParams()
    if (cursor != null) qs.set('cursor', String(cursor))
    qs.set('limit', String(limit))
    return apiClient.get<ListPage>(`/clientes?${qs.toString()}`)
  },

  /** Búsqueda combinada (CLI-03 + CLI-07). */
  search: (query: string, limit = 50) => {
    const qs = new URLSearchParams({ q: query, limit: String(limit) })
    return apiClient.get<SearchResponse>(`/clientes/search?${qs.toString()}`)
  },

  show: (id: number) => apiClient.get<{ client: Client }>(`/clientes/${id}`),

  create: (payload: ClientCreatePayload) =>
    apiClient.post<{ client: Client }>('/clientes', payload),

  update: (id: number, payload: ClientUpdatePayload) =>
    apiClient.patch<{ client: Client }>(`/clientes/${id}`, payload),

  remove: (id: number) => apiClient.delete<void>(`/clientes/${id}`),
}

/**
 * Validación de RFC mexicano en cliente (CLI-04).
 * 3-4 letras (Ñ y & permitidos) + 6 dígitos (AAMMDD) + 3 caracteres de homoclave.
 * Acepta minúsculas; el backend normaliza a uppercase para el hash determinístico.
 */
export const RFC_REGEX = /^[A-Za-zÑñ&]{3,4}\d{6}[A-Za-z0-9]{3}$/

export function validateRfc(rfc: string): string | null {
  const trimmed = rfc.trim()
  if (trimmed === '') return 'El RFC es obligatorio.'
  if (!RFC_REGEX.test(trimmed)) {
    return 'RFC inválido. Formato: 3-4 letras + 6 dígitos AAMMDD + 3 caracteres.'
  }
  return null
}
